import { useState } from "react";
import { updatePart } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X } from "@phosphor-icons/react";

export default function PartForm({ part, onClose, onSaved }) {
  const displayName = part?.Display_Name || part?.["Product Name"] || "";
  const hp = part?.Compressor_HP;
  const voltage = part?.Spec1 || "";
  const productClass = part?.Resolved_Product_Class || "";

  const [form, setForm] = useState({
    list_price: part?.List_Price ?? "",
    list_price_ca: part?.List_Price_CA ?? "",
    max_cfm: part?.["Free Airdelivery_Max Working Pressure (CFM)"] ?? "",
    min_cfm: part?.["Free Airdelivery_Min Working Pressure(CFM)"] ?? "",
    min_psi: part?.["Min Working Pressure(PSI)"] ?? "",
    max_psi: part?.["Max Working Pressure(PSI)"] ?? "",
    width: part?.Width ?? "",
    length: part?.Legth ?? "",
    height: part?.Height ?? "",
    weight: part?.Weight ?? "",
    explanation: part?.Explanation ?? "",
    in_price_list: String(part?.["In Price List"] || "").toUpperCase() === "TRUE",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!part) return null;

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const payload = {};
    const numFields = ["list_price", "list_price_ca", "width", "length", "height", "weight"];
    const strFields = ["max_cfm", "min_cfm", "min_psi", "max_psi", "explanation"];

    for (const f of numFields) {
      if (form[f] !== "" && form[f] != null) payload[f] = parseFloat(form[f]);
    }
    for (const f of strFields) {
      if (form[f] !== "" && form[f] != null) payload[f] = String(form[f]);
    }
    // Always include the in-price-list toggle so admins can flip it from the edit screen.
    payload.in_price_list = form.in_price_list ? "TRUE" : "FALSE";

    try {
      await updatePart(part.ID, payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="part-form-overlay">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white border-l border-slate-200 shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold tracking-tight font-['Chivo']">Edit Part</h2>
          <button data-testid="part-form-close" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        {/* Read-only part identity */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="text-base font-bold text-slate-900 font-['Chivo']">{displayName}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
            {hp != null && <span className="font-bold text-blue-700">{hp} HP</span>}
            {voltage && <span>{voltage}</span>}
            {productClass && <span className="truncate max-w-[180px]">{productClass}</span>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div data-testid="part-form-error" className="bg-red-50 text-red-700 text-sm px-4 py-2 rounded-sm border border-red-200">
              {error}
            </div>
          )}

          {/* In Price List toggle */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Availability</legend>
            <label className="flex items-center justify-between gap-3 px-3 py-2.5 border border-slate-200 rounded-sm cursor-pointer hover:bg-slate-50">
              <div>
                <div className="text-sm font-semibold text-slate-900">In Price List</div>
                <div className="text-[11px] text-slate-500">Visible to non-admin users and included in exports.</div>
              </div>
              <input
                data-testid="part-form-in-price-list"
                type="checkbox"
                checked={!!form.in_price_list}
                onChange={(e) => setForm((f) => ({ ...f, in_price_list: e.target.checked }))}
                className="h-5 w-5 accent-blue-700 cursor-pointer"
              />
            </label>
          </fieldset>

          {/* Prices */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Prices</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="List Price (USD)" testId="part-form-list-price" type="number" step="0.01"
                value={form.list_price} onChange={set("list_price")} prefix="$" />
              <Field label="List Price (CAD)" testId="part-form-list-price-ca" type="number" step="0.01"
                value={form.list_price_ca} onChange={set("list_price_ca")} prefix="C$" />
            </div>
          </fieldset>

          {/* CFM */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Free Air Delivery (CFM)</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max CFM" testId="part-form-max-cfm" value={form.max_cfm} onChange={set("max_cfm")} suffix="CFM" />
              <Field label="Min CFM" testId="part-form-min-cfm" value={form.min_cfm} onChange={set("min_cfm")} suffix="CFM" />
            </div>
          </fieldset>

          {/* PSI */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Working Pressure (PSI)</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min PSI" testId="part-form-min-psi" value={form.min_psi} onChange={set("min_psi")} suffix="PSI" />
              <Field label="Max PSI" testId="part-form-max-psi" value={form.max_psi} onChange={set("max_psi")} suffix="PSI" />
            </div>
          </fieldset>

          {/* Dimensions */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Dimensions</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Width" testId="part-form-width" type="number" step="0.01"
                value={form.width} onChange={set("width")} suffix='"' />
              <Field label="Length" testId="part-form-length" type="number" step="0.01"
                value={form.length} onChange={set("length")} suffix='"' />
              <Field label="Height" testId="part-form-height" type="number" step="0.01"
                value={form.height} onChange={set("height")} suffix='"' />
              <Field label="Weight" testId="part-form-weight" type="number" step="0.01"
                value={form.weight} onChange={set("weight")} suffix="lbs" />
            </div>
          </fieldset>

          {/* Explanation */}
          <fieldset>
            <legend className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Explanation</legend>
            <textarea
              data-testid="part-form-explanation"
              value={form.explanation}
              onChange={set("explanation")}
              rows={3}
              className="w-full rounded-sm border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
            />
          </fieldset>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <Button data-testid="part-form-submit" type="submit" disabled={saving}
              className="flex-1 rounded-sm bg-blue-700 text-white hover:bg-blue-800 font-semibold active:scale-[0.98] transition-all">
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button data-testid="part-form-cancel" type="button" variant="outline" onClick={onClose}
              className="rounded-sm border-slate-200 hover:bg-slate-50">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", step, prefix, suffix }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">{prefix}</span>
        )}
        <Input
          data-testid={testId}
          type={type}
          step={step}
          value={value}
          onChange={onChange}
          className={`rounded-sm border-slate-300 h-9 text-sm ${prefix ? "pl-7" : ""} ${suffix ? "pr-10" : ""}`}
        />
        {suffix && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400">{suffix}</span>
        )}
      </div>
    </div>
  );
}
